window.createVisibilityObserver = function createVisibilityObserver(
  targetClass,
  callback,
  threshold = 0.1
) {
  if (typeof callback !== 'function') {
    return;
  }

  if (typeof threshold !== 'number' || threshold < 0 || threshold > 1) {
    threshold = 0.1;
  }

  function onElementVisible(entry) {
    if (entry.isIntersecting) {
      callback(entry.target);
    }
  }

  const observerOptions = {
    root: null,
    rootMargin: '0px',
    threshold: threshold,
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(onElementVisible);
  }, observerOptions);

  if (document.querySelector(`.${targetClass}`)) {
    document.querySelectorAll(`.${targetClass}`).forEach((el) => {
      observer.observe(el);
    });
  }
};

window.lineTitlesFunc = function lineTitlesFunc(titlesArr, options = {}) {
  const hasDynamicGroups = typeof options.getGroups === 'function';
  let resizeTimeout = null;
  let resizeFrame = null;
  let initTimeout = null;
  let isDestroyed = false;
  let loadListenerAdded = false;

  const toElementsArray = (items) => {
    if (!items) {
      return [];
    }

    return Array.from(items).filter((item) => {
      return item && item.style && typeof item.offsetHeight === 'number';
    });
  };

  const getLineTitlesGroups = () => {
    if (hasDynamicGroups) {
      const groups = options.getGroups();

      if (!groups) {
        return [];
      }

      return Array.from(groups)
        .map(toElementsArray)
        .filter((group) => group.length);
    }

    const elements = toElementsArray(titlesArr);

    return elements.length ? [elements] : [];
  };

  const resetLineTitlesHeight = (groups) => {
    const elements = [];

    groups.forEach((group) => {
      group.forEach((title) => {
        elements.push(title);
      });
    });

    Array.from(new Set(elements)).forEach((title) => {
      title.style.minHeight = '';
    });
  };

  const setLineTitlesHeight = () => {
    if (isDestroyed) {
      return;
    }

    const groups = getLineTitlesGroups();

    resetLineTitlesHeight(groups);

    groups.forEach((group) => {
      let maxHeight = 0;

      group.forEach((title) => {
        const height = title.offsetHeight;

        if (height > maxHeight) {
          maxHeight = height;
        }
      });

      group.forEach((title) => {
        title.style.minHeight = maxHeight + 'px';
      });
    });
  };

  const requestLineTitlesHeight = () => {
    if (isDestroyed) {
      return;
    }

    if (resizeFrame) {
      cancelAnimationFrame(resizeFrame);
    }

    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null;
      setLineTitlesHeight();
    });
  };

  const runLineTitlesHeight = () => {
    if (isDestroyed) {
      return;
    }

    if (initTimeout) {
      clearTimeout(initTimeout);
    }

    initTimeout = setTimeout(() => {
      initTimeout = null;
      requestLineTitlesHeight();
    }, 50);
  };

  const onLineTitlesResize = () => {
    if (isDestroyed) {
      return;
    }

    if (resizeTimeout) {
      return;
    }

    resizeTimeout = setTimeout(() => {
      resizeTimeout = null;
      requestLineTitlesHeight();
    }, 150);
  };

  runLineTitlesHeight();

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(runLineTitlesHeight).catch(runLineTitlesHeight);
  } else {
    loadListenerAdded = true;
    window.addEventListener('load', runLineTitlesHeight, { once: true });
  }

  window.addEventListener('resize', onLineTitlesResize, { passive: true });

  return {
    refresh: requestLineTitlesHeight,
    destroy: () => {
      const groups = getLineTitlesGroups();

      isDestroyed = true;

      if (initTimeout) {
        clearTimeout(initTimeout);
        initTimeout = null;
      }

      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
        resizeTimeout = null;
      }

      if (resizeFrame) {
        cancelAnimationFrame(resizeFrame);
        resizeFrame = null;
      }

      if (loadListenerAdded) {
        window.removeEventListener('load', runLineTitlesHeight);
      }

      window.removeEventListener('resize', onLineTitlesResize);
      resetLineTitlesHeight(groups);
    },
  };
};

const fixedBackgroundMediaQuery = typeof window.matchMedia === 'function'
  ? window.matchMedia('(min-width: 1025px) and (hover: hover) and (pointer: fine)')
  : null;
const fixedBackgroundStates = new WeakMap();
const fixedBackgroundStyleProperties = [
  'background-image',
  'background-position',
  'background-repeat',
  'background-size',
  'background-attachment',
];

const supportsFixedBackground = () => {
  if (!fixedBackgroundMediaQuery || typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
    return false;
  }

  return CSS.supports('background-attachment', 'fixed') && fixedBackgroundMediaQuery.matches;
};

const getPictureBackgroundUrl = (background) => {
  const picture = background.matches('picture') ? background : background.querySelector('picture');
  const sources = Array.from(picture?.querySelectorAll('source') || []);
  const source = sources.find((item) => item.type === 'image/webp' && (item.getAttribute('srcset') || item.getAttribute('data-srcset')))
    || sources.find((item) => item.getAttribute('srcset') || item.getAttribute('data-srcset'));
  const image = picture?.querySelector('img') || (background.matches('img') ? background : background.querySelector('img'));
  const sourceSet = source?.getAttribute('srcset') || source?.getAttribute('data-srcset');
  const sourceUrl = sourceSet?.split(',')[0]?.trim().split(' ')[0];
  const imageUrl = image?.currentSrc || image?.getAttribute('src');
  const url = sourceUrl || imageUrl;

  if (!url) {
    return null;
  }

  try {
    return new URL(url, document.baseURI).href;
  } catch (error) {
    return url;
  }
};

const getFixedBackgroundState = (background, media) => {
  if (!fixedBackgroundStates.has(background)) {
    fixedBackgroundStates.set(background, {
      backgroundStyles: fixedBackgroundStyleProperties.reduce((styles, property) => {
        styles[property] = {
          value: background.style.getPropertyValue(property),
          priority: background.style.getPropertyPriority(property),
        };

        return styles;
      }, {}),
      mediaDisplay: media ? {
        value: media.style.getPropertyValue('display'),
        priority: media.style.getPropertyPriority('display'),
      } : null,
    });
  }

  return fixedBackgroundStates.get(background);
};

const restoreFixedBackgroundState = (background, media, state) => {
  fixedBackgroundStyleProperties.forEach((property) => {
    const originalStyle = state.backgroundStyles[property];

    if (originalStyle.value) {
      background.style.setProperty(property, originalStyle.value, originalStyle.priority);
    } else {
      background.style.removeProperty(property);
    }
  });

  if (!media || !state.mediaDisplay) {
    return;
  }

  if (state.mediaDisplay.value) {
    media.style.setProperty('display', state.mediaDisplay.value, state.mediaDisplay.priority);
  } else {
    media.style.removeProperty('display');
  }
};

const setFixedBackgroundState = (background, isEnabled) => {
  if (!background) {
    return;
  }

  const media = background.matches('picture, img')
    ? background
    : background.querySelector('picture') || background.querySelector('img');
  const state = getFixedBackgroundState(background, media);

  if (!isEnabled) {
    restoreFixedBackgroundState(background, media, state);
    return;
  }

  const imageUrl = getPictureBackgroundUrl(background);

  if (!imageUrl) {
    restoreFixedBackgroundState(background, media, state);
    return;
  }

  background.style.setProperty('background-image', `url("${imageUrl.replace(/"/g, '\\"')}")`);
  background.style.setProperty('background-position', 'center');
  background.style.setProperty('background-repeat', 'no-repeat');
  background.style.setProperty('background-size', 'cover');
  background.style.setProperty('background-attachment', 'fixed');

  if (media) {
    media.style.setProperty('display', 'none');
  }
};

const initFixedBackgrounds = (scope = document) => {
  const backgrounds = scope.querySelectorAll('[data-fixed-background]');

  if (!backgrounds.length) {
    return;
  }

  const isEnabled = supportsFixedBackground();

  backgrounds.forEach((background) => {
    setFixedBackgroundState(background, isEnabled);
  });
};

window.initFixedBackgrounds = initFixedBackgrounds;
initFixedBackgrounds();

let fixedBackgroundResizeFrame = null;

const updateFixedBackgroundsOnMediaChange = () => {
  if (fixedBackgroundResizeFrame !== null) {
    cancelAnimationFrame(fixedBackgroundResizeFrame);
  }

  fixedBackgroundResizeFrame = requestAnimationFrame(() => {
    fixedBackgroundResizeFrame = null;
    initFixedBackgrounds();
  });
};

if (fixedBackgroundMediaQuery?.addEventListener) {
  fixedBackgroundMediaQuery.addEventListener('change', updateFixedBackgroundsOnMediaChange);
} else if (fixedBackgroundMediaQuery?.addListener) {
  fixedBackgroundMediaQuery.addListener(updateFixedBackgroundsOnMediaChange);
}
